<template>
  <div
    ref="myAppCardRef"
    :class="['my-app-card-item__root', isPopoverShow && 'active']"
    @click="onAppClick"
    @mouseenter="onCardItemMouseenter"
    @mouseleave="onCardItemMouseleave"
  >
    <div class="logo">
      <lc-img :imgUrl="props.appData.logo"></lc-img>
    </div>
    <div class="name">{{ props.appData.name }}</div>

    <!-- 我的应用场景 -->
    <IconMyTrash
      v-if="!props.isShowWorkbench && teamStore.hasTopPermission"
      v-show="showIconBtn"
      class="icon-btn"
      @click.stop="onAppDel"
    ></IconMyTrash>

    <!-- 全部应用场景 -->
    <el-popover
      v-if="props.isShowWorkbench"
      popperClass="app-popover"
      placement="bottom-start"
      trigger="hover"
      :showArrow="false"
      :openDelay="200"
      :width="140"
      @show="onPopoverShow"
      @hide="onPopoverHide"
    >
      <template #reference>
        <span
          v-show="isCardItemHover || isPopoverShow"
          ref="dotsRef"
          :class="['icon-btn', isPopoverShow && 'active']"
          @click.stop
        >
          <IconMyIconAppDots></IconMyIconAppDots>
        </span>
      </template>
      <template #default>
        <my-app-prop
          :class="!isPopoverShow && 'app-prop-hide'"
          :appData="props.appData"
          :appRightsData="appRightsData"
          :isShowWorkbench="props.isShowWorkbench"
          @on-app-del="onAppDel"
        ></my-app-prop>
      </template>
    </el-popover>

    <!-- 删除确认弹窗 -->
    <UIDialog v-model="delVisible">
      <template #title>
        <IconMyIconWarn></IconMyIconWarn>
        <span class="del-title">删除应用</span>
      </template>
      <span class="del-tips">删除应用后，全部应用数据将被删除且不可恢复，确定删除请输入：DELETE。</span>
      <el-input v-model="delConfirmTxt" class="del-input" :placeholder="`请输入 “${DEL_CONFIRM_TXT}”`"></el-input>
      <template #footer>
        <base-button type="danger" :disabled="delConfirmTxt !== DEL_CONFIRM_TXT" @click="onDelConfirm">
          删除
        </base-button>
        <base-button type="default" @click="onDelCancel">取消</base-button>
      </template>
    </UIDialog>
  </div>
</template>

<script lang="ts" setup>
import { openInNewTab } from '@/utils/open-in-new-tab';
import { RespApp, apiPostAppDelete } from '@vas2t-lowcode/api';
import { computed, defineAsyncComponent, defineProps, PropType, ref } from 'vue';
import type { TMiddlePageOpenAppQuery } from '@/features/middle-page-open-app/middle-page-open-app';
import { getMiddlePageAppUrl } from '@/features/middle-page-open-app/middle-page-open-app.router';
import type { IMyAppFullData } from './my-app';
import { dwMyAppSingleShow, dwOnAppClick, dwOnAppHover } from './my-app-dw';
import { useRouter } from 'vue-router';
import { useIntersectionObserver } from '@vueuse/core';
import UIDialog from '@vas2t-lowcode/libs/src/components/ui-dialog';
import BaseButton from '@/components/base-button.vue';
import { showMessage } from '@vas2t-lowcode/utils';
import { usePTeamStore } from '@/store';

const props = defineProps({
  /** @type {Object}  应用信息 */
  appData: {
    type: Object as PropType<RespApp>,
    default() {
      return {} as RespApp;
    },
  },
  /** 是否显示工作区-用于工作区所有应用 */
  isShowWorkbench: {
    type: Boolean,
    default: false,
  },
});

const teamStore = usePTeamStore();

const lcImg = defineAsyncComponent(() => import('@/features/lc-img/lc-img.vue'));
const myAppProp = defineAsyncComponent(() => import('@/features/my-app/my-app-prop.vue'));

/** 组件 myAppProp 是否初始化完成 */
const isMyAppPropInit = ref(false);

const isCardItemHover = ref(false);

/** 显示 ...  */
const showIconBtn = computed(() => {
  // 显示条件：1. 鼠标hover&获取到权益信息 2. 属性弹窗显示
  return isCardItemHover.value;
});

const emit = defineEmits<{
  (e: 'show-app-pay-require-dialog', value: IMyAppFullData): void;
  (e: 'on-reload-data'): void;
}>();
const dotsRef = ref<HTMLElement | null>(null);

const isPopoverShow = ref(false);
const onPopoverShow = () => {
  isPopoverShow.value = true;

  dwOnAppHover(props.appData);
};
const onPopoverHide = () => {
  isPopoverShow.value = false;
};

const onCardItemMouseenter = () => {
  isCardItemHover.value = true;
  isMyAppPropInit.value = true;
  // updatePopoverWidth();
};

/** 鼠标移出应用块事件 */
const onCardItemMouseleave = () => {
  isCardItemHover.value = false;
};

/** 弹出层宽度 */
// const popoverWidth = ref(220);

/** app权益信息 */
const appRightsData = computed(() => {
  return props.appData.app_rights;
});

/** 是否有支付权限 */
const hasPayPermission = ref(true);
const isOutOfDate = computed(() => {
  const { appData } = props;
  if (!appData) {
    return;
  }

  const { is_end_time_expired } = appData?.app_rights || {};
  return is_end_time_expired;
});
/** 是否单购 */
const isSinglePayValid = computed(() => {
  const { appData } = props;
  if (!appData) {
    return;
  }

  const { is_app_single_purchase } = appData?.app_rights || {};

  return is_app_single_purchase && !isOutOfDate.value;
});

/** 提示样式 */
const tipClass = computed(() => {
  const { appData } = props;
  if (!appData) {
    return '';
  }

  // 已过期
  const _isOutOfDate = isOutOfDate.value;
  if (_isOutOfDate !== undefined && _isOutOfDate) {
    return 'out-of-date';
  }
  // 已单购
  const _isSinglePay = isSinglePayValid.value;
  if (_isSinglePay !== undefined && _isSinglePay) {
    return 'single-pay';
  }

  return '';
});

/** 更新应用信息 */
// const updatePopoverWidth = () => {
//   const data = appRightsData.value;

//   if (!data) {
//     return;
//   }

//   // 判断是否有支付权限
//   if (!(props.appData.is_ws_admin && !data.is_free)) {
//     hasPayPermission.value = false;
//   }

//   // if (!hasPayPermission.value) {
//   //   popoverWidth.value = 140;
//   //   return;
//   // }

//   // 需要显示支付按钮，宽度需要固定值
//   popoverWidth.value = 220;
// };

const router = useRouter();
/** app 点击弹窗 */
const onAppClick = () => {
  dwOnAppClick(props.appData);

  // const { is_valid_paid_period, is_end_time_expired, is_free } = appRightsData.value;
  // if (!is_valid_paid_period && is_end_time_expired && !is_free) {
  //   // 通知外部展示应用支付提示框
  //   emit('show-app-pay-require-dialog', { appData: props.appData, appRightsData: appRightsData.value });
  //   return;
  // }

  // 打开应用
  const { platform_id, team_id, third_app_id, third_ws_id } = props.appData;
  const queryData = {
    page_type: 'open',
    platform_id,
    team_id,
    third_app_id,
    third_ws_id,
  } as TMiddlePageOpenAppQuery;

  const middlePageOpenApp = getMiddlePageAppUrl(router, queryData) || '';
  openInNewTab({ url: middlePageOpenApp, appid: platform_id, src: '', extraData: queryData });
};

// #region 埋点相关逻辑
const myAppCardRef = ref<HTMLDivElement | null>(null);
const { stop } = useIntersectionObserver(myAppCardRef, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    // 触发埋点并终止监听
    dwMyAppSingleShow(props.appData);
    stop();
  }
});

// 删除我的应用
const delVisible = ref(false);
const delConfirmTxt = ref('');
const DEL_CONFIRM_TXT = 'DELETE';
const onAppDel = () => {
  delVisible.value = true;
};
const onDelConfirm = () => {
  const { platform_id, team_id, third_app_id, third_ws_id } = props.appData;
  if (!platform_id || !team_id || !third_app_id || !third_ws_id) {
    return;
  }
  const params = {
    platform_id,
    team_id,
    third_app_id,
    third_ws_id,
  };
  apiPostAppDelete(params)
    .then(() => {
      // 通知外部同步我的应用数据
      emit('on-reload-data');
      showMessage({ msgContent: '已成功删除', msgType: 'success' });
    })
    .finally(() => {
      delVisible.value = false;
    });
};
const onDelCancel = () => {
  delVisible.value = false;
};
// #endregion
</script>

<style lang="scss" scoped>
.my-app-card-item {
  &__root {
    cursor: pointer;
    position: relative;
    flex: none;
    box-sizing: border-box;
    width: 100px;
    height: 100px;
    // background: var(--lowcode-custom-bg-30);
    border-radius: 8px;
    margin-top: 4px;
    margin-right: 8px;
    text-align: center;

    &:hover,
    &.active {
      background: var(--lowcode-custom-bg-31);
    }

    .logo {
      display: inline-block;
      width: 36px;
      height: 36px;
      margin-top: 22px;
      border-radius: 6px;
      overflow: hidden;
      position: relative;

      .tips {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 12px;
        background: rgba(17, 19, 23, 0.5);
        span {
          letter-spacing: 1px;
          display: inline-block;
          color: #ffffff;
          height: 16px;
          line-height: 16px;
          font-size: 16px;
          transform: scale(0.5);
          width: 72px;
          transform-origin: 0 3px;
        }

        &.out-of-date {
          background-color: rgba(17, 19, 23, 0.5);
        }

        &.single-pay {
          background-color: rgba(255, 152, 15, 1);
        }
      }
    }

    .name {
      padding: 0 8px;
      margin-top: 6px;
      height: 32px;
      font-size: 12px;
      color: var(--lowcode-text-02);
      line-height: 16px;
      width: 84px;
      @extend %lowcode-two-line;
    }

    .icon-btn {
      width: 16px;
      height: 16px;
      position: absolute;
      right: 8px;
      top: 8px;
      cursor: pointer;
      border-radius: 2px;
      padding: 1px;

      :deep(path) {
        fill: var(--lowcode-icon-04);
      }

      &::before {
        content: '';
        position: absolute;
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
      }

      &:hover {
        background-color: var(--lowcode-status-003);
        :deep(path) {
          fill: var(--lowcode-icon-03);
        }
      }

      &:active,
      &.active {
        background-color: var(--lowcode-status-03);
        :deep(path) {
          fill: var(--lowcode-icon-02);
        }
      }
    }
  }
}

// 应用删除弹窗
.del-title {
  color: var(--lowcode-error-01);
  margin-left: 8px;
}
.del-tips {
  color: var(--lowcode-text-03);
  font-size: 14px;
  font-family: MicrosoftYaHei;
  color: #444e63;
  line-height: 22px;
}
.del-input {
  margin-top: 16px;
  ::v-deep .el-input__wrapper {
    padding: 0;
    border-radius: 2px;
  }
}
</style>

<style lang="scss">
.el-popover.el-popper {
  &.app-popover {
    box-sizing: border-box;
    box-shadow: 0;
    border: 0;
    padding: 0;
    min-width: 140px;
    background: var(--lowcode-dlg-background-01);
    margin-top: -10px !important;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
    border-radius: 2px;
  }

  .app-prop-hide {
    opacity: 0;
    border-radius: 2px;
    border: 1px solid rgba(38, 46, 62, 0.15);
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  }
}
</style>
